import { useState, useEffect } from 'react';
import { productsApi, Product, StockDetailResponse } from '../../api/products.api';
import { categoriesApi, Category } from '../../api/categories.api';
import { modelsApi, Model } from '../../api/models.api';
import { brandsApi, Brand } from '../../api/brands.api';
import { branchesApi, Branch } from '../../api/branches.api';
import Table, { Column } from '../../components/common/Table';
import Button from '../../components/forms/Button';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import Input from '../../components/forms/Input';
import Select from '../../components/forms/Select';

const ProductList = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStockDetailModalOpen, setIsStockDetailModalOpen] = useState(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [mergingProduct, setMergingProduct] = useState<Product | null>(null);
  const [targetProductId, setTargetProductId] = useState<string>('');
  const [stockDetail, setStockDetail] = useState<StockDetailResponse | null>(null);
  const [stockDetailLoading, setStockDetailLoading] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  
  // Filter and pagination state
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [selectedBranch, setSelectedBranch] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 50;
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    parentCategory: '',
    modelId: '',
  });

  useEffect(() => {
    fetchProducts();
  }, [currentPage, selectedCategory, selectedBrand, selectedBranch, searchQuery]);

  useEffect(() => {
    fetchCategories();
    fetchBrands();
    fetchBranches();
  }, []);

  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      const response = await productsApi.getAllPaginated(
        currentPage, 
        itemsPerPage, 
        selectedCategory !== 'all' ? selectedCategory : undefined,
        selectedBrand !== 'all' ? selectedBrand : undefined,
        selectedBranch !== 'all' ? selectedBranch : undefined,
        searchQuery || undefined
      );
      setProducts(response.data);
      setTotalPages(response.totalPages);
      setTotalItems(response.total);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Ürünler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await categoriesApi.getAll();
      setCategories(data.filter((c) => c.isActive));
    } catch (err: any) {
      console.error('Kategoriler yüklenirken hata:', err);
    }
  };

  const fetchBrands = async () => {
    try {
      const data = await brandsApi.getAll();
      setBrands(data.filter((b) => b.isActive));
    } catch (err: any) {
      console.error('Markalar yüklenirken hata:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const data = await branchesApi.getAll();
      setBranches(data.filter((b) => b.isActive));
    } catch (err: any) {
      console.error('Şubeler yüklenirken hata:', err);
    }
  };

  const handleViewStockDetail = async (product: Product) => {
    setIsStockDetailModalOpen(true);
    setStockDetailLoading(true);
    setStockDetail(null);
    
    try {
      const detail = await productsApi.getStockDetail(product.id);
      setStockDetail(detail);
    } catch (err: any) {
      setError(err.message || 'Stok detayı yüklenirken hata oluştu');
    } finally {
      setStockDetailLoading(false);
    }
  };

  const handleFilterCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleBrandChange = (brand: string) => {
    setSelectedBrand(brand);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleBranchChange = (branch: string) => {
    setSelectedBranch(branch);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleClearFilters = () => {
    setSelectedCategory('all');
    setSelectedBrand('all');
    setSelectedBranch('all');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleOpenModal = async (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      const categoryId =
        typeof product.category === 'object' && product.category !== null
          ? (product.category as any)._id || (product.category as any).id
          : product.category;

      // Find parent category
      const cat = categories.find(c => c.id === categoryId);
      const parentCategoryId = cat?.parentCategory?._id || (cat?.parentCategory as any)?.id || '';

      // Load models for the category first
      if (categoryId) {
        const modelsData = await loadModelsForCategory(categoryId);
        
        // Find the model that matches the product's model name and brand
        const productBrandId = typeof product.brand === 'object' && product.brand !== null
          ? (product.brand as any)._id || (product.brand as any).id
          : product.brand;
        
        const matchingModel = modelsData.find(m => 
          m.name === product.model && 
          ((m.brand as any)?.id === productBrandId || (m.brand as any)?._id === productBrandId)
        );

        setFormData({
          name: product.name,
          category: categoryId,
          parentCategory: parentCategoryId,
          modelId: matchingModel?.id || (matchingModel as any)?._id || '',
        });
      } else {
        setFormData({
          name: product.name,
          category: categoryId,
          parentCategory: parentCategoryId,
          modelId: '',
        });
      }
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        category: '',
        parentCategory: '',
        modelId: '',
      });
      setModels([]);
    }
    setIsModalOpen(true);
  };

  const loadModelsForCategory = async (categoryId: string): Promise<Model[]> => {
    console.log('Loading models for category:', categoryId);
    try {
      const modelsData = await modelsApi.getByCategory(categoryId);
      console.log('Models loaded:', modelsData);
      setModels(modelsData);
      return modelsData;
    } catch (err) {
      console.error('Modeller yüklenirken hata:', err);
      setModels([]);
      return [];
    }
  };

  const handleParentCategoryChange = (parentCategoryId: string) => {
    setFormData({ 
      ...formData, 
      parentCategory: parentCategoryId,
      category: '',
      modelId: '',
    });
    setModels([]);
  };

  const handleCategoryChange = async (categoryId: string) => {
    setFormData({ 
      ...formData, 
      category: categoryId,
      modelId: '',
    });
    
    if (categoryId) {
      await loadModelsForCategory(categoryId);
    } else {
      setModels([]);
    }
  };

  const handleModelChange = (modelId: string) => {
    setFormData({ 
      ...formData, 
      modelId,
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const selectedModel = models.find(m => m.id === formData.modelId || (m as any)._id === formData.modelId);
      
      const submitData = {
        name: formData.name,
        category: formData.category,
        brand: (selectedModel?.brand as any)?._id || (selectedModel?.brand as any)?.id,
        model: selectedModel?.name,
      };

      if (editingProduct) {
        await productsApi.update(editingProduct.id, submitData);
      } else {
        await productsApi.create(submitData);
      }
      await fetchProducts();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'İşlem başarısız oldu');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      await productsApi.delete(id);
      await fetchProducts();
    } catch (err: any) {
      setError(err.message || 'Silme işlemi başarısız oldu');
    }
  };

  const handleOpenMergeModal = (product: Product) => {
    setMergingProduct(product);
    setTargetProductId('');
    setIsMergeModalOpen(true);
  };

  const handleCloseMergeModal = () => {
    setIsMergeModalOpen(false);
    setMergingProduct(null);
    setTargetProductId('');
  };

  const handleMergeProducts = async () => {
    if (!mergingProduct || !targetProductId) {
      setError('Lütfen hedef ürünü seçin');
      return;
    }

    if (mergingProduct.id === targetProductId) {
      setError('Bir ürün kendisiyle birleştirilemez');
      return;
    }

    if (!window.confirm(`"${mergingProduct.name}" ürününü seçilen ürüne birleştirmek istediğinizden emin misiniz? Bu işlem geri alınamaz!`)) {
      return;
    }

    try {
      const result = await productsApi.mergeProducts(mergingProduct.id, targetProductId);
      alert(`Başarılı! ${result.devicesMovedCount} cihaz taşındı.`);
      handleCloseMergeModal();
      await fetchProducts();
    } catch (err: any) {
      setError(err.message || 'Birleştirme işlemi başarısız oldu');
    }
  };

  const getCategoryName = (category: any): string => {
    if (!category) return '-';
    
    // If category is already populated as an object
    if (typeof category === 'object' && category !== null) {
      return category.name || '-';
    }
    
    // If category is a string ID, try to find it in the categories list
    if (typeof category === 'string') {
      const cat = categories.find((c) => c.id === category);
      return cat?.name || '-';
    }
    
    return '-';
  };

  const getBrandName = (brand: any): string => {
    if (!brand) return '-';
    if (typeof brand === 'object' && brand !== null) {
      return brand.name || '-';
    }
    // If it's a string (ID), try to find in brands list
    const brandObj = brands.find((b) => b.id === brand);
    return brandObj?.name || brand || '-';
  };

  const columns: Column<Product>[] = [
    { key: 'name', header: 'Ürün Adı' },
    { key: 'brand', header: 'Marka', render: (item) => getBrandName(item.brand) },
    { key: 'model', header: 'Model', render: (item) => item.model || '-' },
    { key: 'category', header: 'Kategori', render: (item) => getCategoryName(item.category) },
    {
      key: 'stock',
      header: 'Stok',
      render: (item) => {
        const count = (item as any).deviceCount || 0;
        return (
          <span className="font-semibold text-gray-900">
            {count}
          </span>
        );
      },
    },
    {
      key: 'isActive',
      header: 'Durum',
      render: (item) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {item.isActive ? 'Aktif' : 'Pasif'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'İşlemler',
      render: (item) => (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => handleViewStockDetail(item)}>
            Detay
          </Button>
          <Button size="sm" variant="secondary" onClick={() => handleOpenModal(item)}>
            Düzenle
          </Button>
          <Button size="sm" variant="secondary" onClick={() => handleOpenMergeModal(item)}>
            Birleştir
          </Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(item.id)}>
            Sil
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text="Ürünler yükleniyor..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Ürünler</h1>
        <Button onClick={() => handleOpenModal()}>Yeni Ürün Ekle</Button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Filtreler</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div className="mb-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ürün Adı
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Ürün adı ile ara..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="mb-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kategori
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => handleFilterCategoryChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tüm Kategoriler</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Marka
            </label>
            <select
              value={selectedBrand}
              onChange={(e) => handleBrandChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tüm Markalar</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Şube
            </label>
            <select
              value={selectedBranch}
              onChange={(e) => handleBranchChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tüm Şubeler</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-0">
            <Button 
              variant="outline" 
              onClick={handleClearFilters} 
              fullWidth
            >
              Temizle
            </Button>
          </div>
        </div>
      </div>

      <Table
        data={products}
        columns={columns}
        emptyMessage="Henüz ürün bulunmamaktadır"
        pagination={{
          currentPage,
          totalPages,
          totalItems,
          itemsPerPage,
          onPageChange: handlePageChange,
        }}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <Input
            label="Ürün Adı"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Örn: Dell Latitude 5420 Dizüstü Bilgisayar"
            required
          />
          <Select
            label="Üst Kategori"
            value={formData.parentCategory}
            onChange={(value) => handleParentCategoryChange(value)}
            options={categories.filter(c => !c.parentCategory).map((c) => ({ value: c.id, label: c.name }))}
            placeholder="Seçiniz"
            required
          />
          <Select
            label="Alt Kategori"
            value={formData.category}
            onChange={(value) => handleCategoryChange(value)}
            options={categories
              .filter(c => c.parentCategory?._id === formData.parentCategory)
              .map((c) => ({ value: c.id, label: c.name }))}
            placeholder="Seçiniz"
            required
            disabled={!formData.parentCategory}
          />
          <Select
            label="Model"
            value={formData.modelId}
            onChange={(value) => handleModelChange(value)}
            options={models.map((m) => ({ 
              value: m.id, 
              label: `${m.name}${m.brand ? ` (${m.brand.name})` : ''}` 
            }))}
            placeholder="Seçiniz"
            required
            disabled={!formData.category}
          />
          {formData.modelId && models.find(m => m.id === formData.modelId)?.brand && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-800">
                ✓ Marka: <span className="font-semibold">{models.find(m => m.id === formData.modelId)?.brand?.name}</span>
              </p>
            </div>
          )}
          <div className="flex gap-2 mt-6">
            <Button type="submit" fullWidth>
              {editingProduct ? 'Güncelle' : 'Ekle'}
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={handleCloseModal}>
              İptal
            </Button>
          </div>
        </form>
      </Modal>

      {/* Stock Detail Modal */}
      <Modal
        isOpen={isStockDetailModalOpen}
        onClose={() => setIsStockDetailModalOpen(false)}
        title="Stok Detayı"
        size="xl"
      >
        {stockDetailLoading ? (
          <Loading text="Stok detayı yükleniyor..." />
        ) : stockDetail ? (
          <div>
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {stockDetail.product.name}
              </h3>
              <div className="text-sm text-gray-600">
                <span className="font-medium">Marka:</span> {stockDetail.product.brand || '-'} | 
                <span className="font-medium ml-2">Model:</span> {stockDetail.product.model || '-'} | 
                <span className="font-medium ml-2">Toplam Cihaz:</span> {stockDetail.totalDevices}
              </div>
            </div>

            {stockDetail.branches.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Bu ürüne ait cihaz bulunmamaktadır
              </div>
            ) : (
              <div className="space-y-6">
                {stockDetail.branches.map((branch) => (
                  <div key={branch.branchId} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
                      <h4 className="font-semibold text-gray-900">{branch.branchName}</h4>
                      <div className="mt-2 flex gap-4 text-sm">
                        <span className="text-gray-600">
                          <span className="font-medium">Toplam:</span> {branch.total}
                        </span>
                        <span className="text-blue-600">
                          <span className="font-medium">Şube Stoğu:</span> {branch.inStock}
                        </span>
                        <span className="text-green-600">
                          <span className="font-medium">Zimmetli:</span> {branch.assigned}
                        </span>
                        <span className="text-yellow-600">
                          <span className="font-medium">Onarımda:</span> {branch.inRepair}
                        </span>
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Seri No
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Durum
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Kondisyon
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Konum
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {branch.devices.map((device) => (
                            <tr key={device.id}>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {device.serialNumber}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  device.status === 'Available' ? 'bg-green-100 text-green-800' :
                                  device.status === 'In Use' ? 'bg-blue-100 text-blue-800' :
                                  device.status === 'Under Repair' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {device.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {device.condition}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {device.location}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6">
              <Button 
                variant="outline" 
                fullWidth 
                onClick={() => setIsStockDetailModalOpen(false)}
              >
                Kapat
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Stok detayı yüklenemedi
          </div>
        )}
      </Modal>

      {/* Merge Products Modal */}
      <Modal
        isOpen={isMergeModalOpen}
        onClose={handleCloseMergeModal}
        title="Ürün Birleştir"
        size="md"
      >
        {mergingProduct && (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>Uyarı:</strong> Bu işlem geri alınamaz. Seçilen ürün silinecek ve tüm cihazları hedef ürüne taşınacaktır.
              </p>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Kaynak Ürün (Silinecek)
              </label>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold text-gray-900">{mergingProduct.name}</p>
                <p className="text-sm text-gray-600">
                  Cihaz Sayısı: {mergingProduct.deviceCount || 0}
                </p>
              </div>
            </div>

            <Select
              label="Hedef Ürün (Cihazlar buraya taşınacak)"
              value={targetProductId}
              onChange={(value) => setTargetProductId(value)}
              options={products
                .filter(p => p.id !== mergingProduct.id)
                .map(product => ({
                  value: product.id,
                  label: `${product.name} - ${typeof product.brand === 'object' && product.brand?.name ? product.brand.name : product.brand || 'Marka Yok'} (${product.deviceCount || 0} cihaz)`
                }))}
              placeholder="Hedef ürünü seçin..."
              searchable={true}
              required
            />

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                fullWidth
                onClick={handleCloseMergeModal}
              >
                İptal
              </Button>
              <Button
                variant="danger"
                fullWidth
                onClick={handleMergeProducts}
                disabled={!targetProductId}
              >
                Birleştir
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProductList;
